import React, { memo, useCallback, useEffect } from 'react'
import tw from 'twin.macro'
import { Outlet, useParams } from 'react-router-dom'
import { Typography } from '@mui/material'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import { Skeleton } from 'src/components/shared/Skeleton'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { AdminFetchCustomer, AdminFetchCustomerProducts } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import { getCustomer, getCustomerLoading } from 'src/selectors/customer'
import {
  getCustomerProductsError,
  getCustomerProductsLoadedPartnerId,
} from 'src/selectors/customerProduct'

const CustomerLayoutComponent = () => {
  const { customerId } = useParams<{ customerId: string }>()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const { isAuthenticated } = useAuthSession()
  const customer = useSelector(getCustomer)
  const isLoading = useSelector(getCustomerLoading)

  const productsLoadedPartnerId = useSelector(getCustomerProductsLoadedPartnerId)
  const productsError = useSelector(getCustomerProductsError)

  // Without `partner_products_view` the products GET would 403. Skip it; product-gated controls
  // then read the slice as "not loaded" and stay hidden (Webhooks "Send test").
  const canViewProducts = useHasPermission(InternalPermission.PartnerProductsView)

  const fetchCustomer = useCallback(() => {
    if (!customerId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCustomer.request({ customerId }))
  }, [customerId, asyncDispatchWithErrorToast])

  /**
   * Products are fetched here rather than by the Products tab, because product entitlement
   * gates what other tabs may offer — the Webhooks tab's "send webhook" control reads it. The
   * outlet below waits for this, so no tab can evaluate a gate against an empty list and
   * wrongly conclude "not entitled".
   *
   * Silent: a failure is surfaced by the inline banner below and by closed gates, so a toast
   * on top of that is noise.
   */
  const fetchProducts = useCallback(() => {
    if (!customerId || !canViewProducts) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchCustomerProducts.request({ partnerId: customerId }), true)
  }, [asyncDispatchWithErrorToast, canViewProducts, customerId])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    fetchCustomer()
    fetchProducts()
  }, [fetchCustomer, fetchProducts, isAuthenticated])

  // Only ever true on the very first customer load: a refetch of an already-loaded customer —
  // the header's onboarding action, or a background token refresh — keeps the previous record
  // in state, so this never closes the gate under an admin mid-work.
  const isCustomerPending = isLoading && !customer

  /**
   * "Settled for THIS customer", not "a request is in flight" — see the products slice's
   * `loadedPartnerId`. Gating on `loading` would unmount every tab on a background token
   * refresh, since that changes `isAuthenticated` and re-runs the fetch effect above. It also covers
   * the window before REQUEST is dispatched on a customer switch, when the slice still holds
   * the previous customer's rows.
   *
   * A FAILED fetch settles too — it records `loadedPartnerId` and clears the rows — so a
   * failure opens the outlet on its own, with the banner above it and every gate reading
   * closed. Nothing here need consult `productsError`: doing so would open the outlet for a
   * newly selected customer while the error still belongs to the previous one.
   * Without partner_products_view the fetch is skipped, so there is nothing to settle.
   */
  // Without the permission there is nothing to wait for — the fetch is never dispatched.
  const haveProductsSettled = !canViewProducts || productsLoadedPartnerId === customerId
  const isTabContentPending = !haveProductsSettled || isCustomerPending

  return (
    <div css={tw`flex min-w-0 flex-1 flex-col overflow-hidden`}>
      {productsError ? (
        <div
          data-testid="admin-customer-products-error"
          css={tw`flex flex-row items-center gap-3 border-0 border-b border-solid border-shades-100 bg-shades-50 px-6 py-3`}
        >
          <Typography variant="body2" css={tw`text-shades-700`}>
            {/* The separator is not decoration: backend messages carry no terminal
                punctuation, so without it this reads as one run-on sentence. */}
            {`Could not load this customer's products: ${productsError} — product-gated actions stay disabled until this succeeds.`}
          </Typography>
          <div css={tw`ml-auto`}>
            <Button
              size="tiny"
              color="white"
              onClick={fetchProducts}
              data-testid="admin-customer-products-retry-button"
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}
      <div css={tw`min-h-0 flex-1 overflow-y-auto`}>
        {/* The outlet is withheld until products settle. On a products failure it renders
            anyway, above with a banner and with every gate closed — blocking permanently would
            lock the whole customer area out of tabs that do not care about products.

            One placeholder covers both fetches: they are dispatched together in the same
            effect, so "customer" and "products" can no longer usefully name different waits. */}
        {isTabContentPending ? (
          <div css={tw`p-6`} data-testid="admin-customer-loading">
            <Skeleton className={tw`mb-5 h-8 w-56`} />
            <SectionCardSkeleton fields={3} />
          </div>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  )
}

export const CustomerLayout = memo(CustomerLayoutComponent)
